import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuest } from '@/contexts/QuestContext';
import { QUEST_STEP_IDS } from '@/types/quest-types';

/**
 * Hook to detect quest completion based on user actions
 * This should be used on pages where quest steps can be completed
 */
export const useQuestDetection = () => {
  const location = useLocation();
  const { questState, currentQuestStep, completeQuest, isOnCorrectPage } = useQuest();

  // Listen for quest completion events
  useEffect(() => {
    if (!questState.isActive || !currentQuestStep || questState.isCompleted) {
      return;
    }

    // Only listen for events on the correct page for the current quest step
    if (!isOnCorrectPage(currentQuestStep)) {
      return;
    }

    const handleQuestCompletion = (event: CustomEvent) => {
      const { stepId, action } = event.detail;
      
      console.log(`[QuestDetection] Received quest completion event for step ${stepId}, current step: ${currentQuestStep.id}, action: ${action}`);
      
      // Verify this is the current quest step
      if (stepId === currentQuestStep.id) {
        console.log(`[QuestDetection] Quest step ${stepId} completed with action: ${action}`);
        completeQuest(stepId);
      } else {
        console.log(`[QuestDetection] Ignoring quest completion for step ${stepId} (not current step ${currentQuestStep.id})`);
      }
    };

    // Listen for quest completion events
    window.addEventListener('quest-step-completed', handleQuestCompletion as EventListener);

    return () => {
      window.removeEventListener('quest-step-completed', handleQuestCompletion as EventListener);
    };
  }, [questState, currentQuestStep, completeQuest, isOnCorrectPage]);

  // Helper function to trigger quest completion
  const triggerQuestCompletion = (stepId: number, action: string) => {
    const event = new CustomEvent('quest-step-completed', {
      detail: { stepId, action }
    });
    window.dispatchEvent(event);
  };

  return {
    triggerQuestCompletion,
    currentQuestStep,
    isQuestActive: questState.isActive,
    isOnCorrectPage: currentQuestStep ? isOnCorrectPage(currentQuestStep) : false
  };
};

/**
 * Helper function to trigger quest completion from anywhere in the app
 */
export const triggerQuestStepCompletion = (stepId: number, action: string) => {
  console.log(`[QuestDetection] Triggering quest completion for step ${stepId} with action: ${action}`);
  const event = new CustomEvent('quest-step-completed', {
    detail: { stepId, action }
  });
  window.dispatchEvent(event);
};

/**
 * Quest step completion triggers for common actions
 */
export const QuestTriggers = {
  // Step 1: Create Page
  pageCreated: () => triggerQuestStepCompletion(QUEST_STEP_IDS.CREATE_PAGE, 'page_created'),
  
  // Step 2: Generate Research
  researchGenerated: () => triggerQuestStepCompletion(QUEST_STEP_IDS.GENERATE_RESEARCH, 'research_generated'),
  
  // Step 3: Generate Image
  imageGenerated: () => triggerQuestStepCompletion(QUEST_STEP_IDS.GENERATE_IMAGE, 'image_generated'),
  
  // Step 4: Create Project
  projectCreated: () => triggerQuestStepCompletion(QUEST_STEP_IDS.CREATE_PROJECT, 'project_created'),
  
  // Step 5: Add Task
  taskAdded: () => triggerQuestStepCompletion(QUEST_STEP_IDS.ADD_TASK, 'task_added'),
  
  // Step 6: Import Page
  pageImported: () => triggerQuestStepCompletion(QUEST_STEP_IDS.IMPORT_PAGE, 'page_imported'),
  
  // Step 7: Import Research
  researchImported: () => triggerQuestStepCompletion(QUEST_STEP_IDS.IMPORT_RESEARCH, 'research_imported'),
  
  // Step 8: Add Team Member
  teamMemberAdded: () => triggerQuestStepCompletion(QUEST_STEP_IDS.ADD_TEAM_MEMBER, 'team_member_added'),
};